import { DatePipe } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { ChatWidgetService } from '../../core/services/chat-widget.service';
import { ComplaintService } from '../../core/services/complaint.service';
import { OpsService } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';
import { DocumentOcrService } from '../document-ocr/document-ocr.service';
import { Segment, splitHighlight } from './complaint-highlight';

interface CaseAttachmentView {
  id: string;
  fileName: string;
  kind: string;
}

/** Groups the workflow statuses into a handful of visual tones for the status pill. */
const STATUS_TONE: Record<string, 'info' | 'progress' | 'good' | 'bad' | 'attn'> = {
  submitted: 'info',
  pending_verification: 'info',
  under_review: 'info',
  routed: 'progress',
  investigation_completed: 'progress',
  awaiting_hearing: 'progress',
  scheduled: 'progress',
  heard: 'progress',
  resolved: 'good',
  closed: 'good',
  rejected: 'bad',
  reopened: 'attn',
};

function statusTone(status: string): string {
  return STATUS_TONE[status] || 'info';
}

@Component({
  selector: 'app-complaint-detail',
  standalone: true,
  imports: [RouterLink, DatePipe],
  templateUrl: './complaint-detail.component.html',
  styleUrl: './complaint-detail.component.scss',
})
export class ComplaintDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(ComplaintService);
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);
  private readonly ocr = inject(DocumentOcrService);
  private readonly chatWidget = inject(ChatWidgetService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  private readonly id = toSignal(this.route.paramMap.pipe(map((p) => p.get('id') ?? '')), {
    initialValue: '',
  });

  /** Present only when arriving from Case Search (`?highlight=…`). */
  private readonly highlightTerm = toSignal(
    this.route.queryParamMap.pipe(map((p) => (p.get('highlight') ?? '').trim())),
    { initialValue: '' },
  );

  readonly complaint = computed(() => this.store.getById(this.id()));
  readonly isDm = computed(() => this.rbac.role() === 'dm');
  readonly canWorkflow = computed(() => this.rbac.canModule('workflow'));
  readonly attachments = signal<CaseAttachmentView[]>([]);

  /** Split text for search-term highlighting; no-op when there is no highlight query. */
  hl(text: string | null | undefined): Segment[] {
    return splitHighlight(text ?? '', this.highlightTerm());
  }

  /** Set only for cases confirmed from the AI intake pipeline — see routes/intake.js confirm. */
  readonly sourceScanBatchId = computed<number | null>(() => {
    const meta = this.complaint()?.metadataJson as { sourceExtraction?: { batchId?: number } } | null | undefined;
    return meta?.sourceExtraction?.batchId ?? null;
  });

  /** The extraction (not the batch) is what view/download actually fetch — a batch can hold
   *  several back-to-back applications, and this case should only ever show its own pages. */
  readonly sourceScanExtractionId = computed<number | null>(() => {
    const meta = this.complaint()?.metadataJson as { sourceExtraction?: { extractionId?: number } } | null | undefined;
    return meta?.sourceExtraction?.extractionId ?? null;
  });

  /** The rest of what Gemini extracted — none of this has its own column on Complaint, it all lives in metadataJson. */
  readonly aiDetails = computed(() => {
    const meta = this.complaint()?.metadataJson as Record<string, unknown> | null | undefined;
    if (!meta?.['sourceExtraction']) return null;
    const location = [meta['village'], meta['gramPanchayat'], meta['tehsil'], meta['vikasKhand']]
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
      .join(', ');
    return {
      relativeName: (meta['relativeName'] as string) || null,
      mobile: (meta['complainantMobile'] as string) || null,
      location: location || null,
      thana: (meta['thana'] as string) || null,
      araziKhasraNo: (meta['araziKhasraNo'] as string) || null,
      forwardedTo: (meta['forwardedTo'] as string) || null,
      subjectHindi: (meta['subjectHindi'] as string) || null,
      summaryHindi: (meta['summaryHindi'] as string) || null,
    };
  });
  readonly message = signal<string | null>(null);
  readonly statusTone = statusTone;

  readonly previewOpen = signal(false);
  readonly previewLoading = signal(false);
  readonly previewUrl = signal<string | null>(null);
  readonly previewKind = signal<'image' | 'pdf' | 'unsupported' | null>(null);
  /** iframe src is a RESOURCE_URL sink in Angular's sanitizer — this blob URL is our own fetch, safe to trust. */
  readonly safePreviewUrl = computed(() => {
    const url = this.previewUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      const url = this.previewUrl();
      if (url) URL.revokeObjectURL(url);
    });
    effect(() => {
      const id = this.id();
      if (id) void this.store.loadById(id);
    });
    effect(() => {
      const item = this.complaint();
      if (item) void this.loadAttachments(item.id);
      else this.attachments.set([]);
    });
  }

  askAboutCase(): void {
    const item = this.complaint();
    if (!item) return;

    const caseRef = String(item.trackingId || item.id);
    const lines = [
      `Case ID: ${caseRef}`,
      `Filed by: ${item.complainantName || item.citizenName || '—'}`,
      `Department: ${item.department || '—'}`,
      `Case type: ${item.category || '—'}`,
      `District: ${item.district || '—'}`,
      `Subject: ${item.subject || '—'}`,
      `Details: ${item.description || '—'}`,
      '',
      'Question: Summarize this case and highlight the key points.',
    ];
    this.chatWidget.openForCase(item.id, lines.join('\n'));
  }

  async openAttachment(file: CaseAttachmentView): Promise<void> {
    try {
      await this.ops.downloadCaseAttachment(file.id, file.fileName);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Could not open attachment');
    }
  }

  async downloadSourceScan(): Promise<void> {
    const extractionId = this.sourceScanExtractionId();
    if (!extractionId) return;
    try {
      await this.ocr.downloadExtractionSourceFile(extractionId, `complaint-${this.id()}-source`);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Could not download the original scan');
    }
  }

  /** Shows the scan in an in-page modal (image or PDF) instead of forcing a download or leaving
   *  for a new tab — the officer stays on this case while looking at it. Cropped to just this
   *  case's own pages — the source upload may have held several back-to-back applications. */
  async viewSourceScan(): Promise<void> {
    const extractionId = this.sourceScanExtractionId();
    if (!extractionId) return;
    this.previewOpen.set(true);
    this.previewLoading.set(true);
    this.previewKind.set(null);
    try {
      const { url, mimeType } = await this.ocr.getExtractionSourcePreview(extractionId);
      this.previewUrl.set(url);
      if (mimeType.startsWith('image/')) {
        this.previewKind.set('image');
      } else if (mimeType === 'application/pdf') {
        this.previewKind.set('pdf');
      } else {
        this.previewKind.set('unsupported');
      }
    } catch (err) {
      this.previewOpen.set(false);
      this.message.set(err instanceof Error ? err.message : 'Could not open the original scan');
    } finally {
      this.previewLoading.set(false);
    }
  }

  closePreview(): void {
    this.previewOpen.set(false);
    const url = this.previewUrl();
    if (url) URL.revokeObjectURL(url);
    this.previewUrl.set(null);
    this.previewKind.set(null);
  }

  private async loadAttachments(caseId: string | number): Promise<void> {
    try {
      const items = await this.ops.listComplaintDocuments(caseId);
      this.attachments.set(
        items.map((d) => ({
          id: d.id,
          fileName: d.fileName,
          kind: d.kind || (d.fileName?.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Image'),
        })),
      );
    } catch {
      this.attachments.set([]);
    }
  }
}
